import React, { useState, useRef, Fragment, Component } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { XIcon, SearchIcon, CheckIcon, UserPlusIcon, ChevronRightIcon, ChevronLeftIcon, ConstructionIcon, TreePineIcon, Building2Icon, TrashIcon, DropletIcon, LightbulbIcon, CarIcon, PawPrintIcon, MapPinIcon, CrosshairIcon, PaperclipIcon, FileIcon, UploadCloudIcon } from 'lucide-react';
interface CreateRequeteDialogProps {
  onClose: () => void;
}
const STEPS = ['Demandeur', 'Catégorie', 'Sous-catégorie', 'Lieu', 'Détails'];
const SAMPLE_USERS = [{
  name: 'LudikSearch BcitiTest',
  email: 'dogan.erisen+28032025@hotmail.com',
  phone: '(778) 927-6462',
  avatar: 'https://i.pravatar.cc/80?img=12'
}, {
  name: 'Sarah Connor',
  email: 'dogan.erisen@hotmail.com',
  phone: '(111) 111-1111',
  avatar: 'https://i.pravatar.cc/80?img=45'
}, {
  name: 'Belanor Waesceran',
  email: 'belanor.w@hotmail.com',
  phone: '(514) 555-0199',
  avatar: 'https://i.pravatar.cc/80?img=33'
}];
interface Category {
  id: string;
  name: string;
  desc: string;
  icon: ComponentType<{
    className?: string;
  }>;
  internal?: boolean;
  subcategories: string[];
}
const CATEGORIES: Category[] = [{
  id: 'voirie',
  name: 'Voirie et infrastructures routières',
  desc: 'Nids-de-poule, trottoirs, signalisation et chaussée',
  icon: ConstructionIcon,
  subcategories: ['Nid-de-poule', 'Trottoir endommagé', 'Affaissement de chaussée', 'Marquage au sol effacé', 'Bordure de rue à réparer']
}, {
  id: 'parcs',
  name: 'Parcs et espaces récréatifs',
  desc: 'Espaces verts, mobilier urbain et aires de jeux',
  icon: TreePineIcon,
  subcategories: ['Jeux pour enfants endommagés', 'Mobilier de parc à réparer', 'Entretien des espaces verts', 'Arbre dangereux ou tombé', 'Terrain sportif à entretenir']
}, {
  id: 'eclairage',
  name: 'Éclairage public',
  desc: 'Lampadaires et feux de circulation',
  icon: LightbulbIcon,
  subcategories: ['Lampadaire défectueux', 'Feu de circulation en panne', 'Éclairage de parc défectueux']
}, {
  id: 'eau',
  name: 'Aqueduc et égouts',
  desc: 'Fuites, refoulements et bornes-fontaines',
  icon: DropletIcon,
  subcategories: ["Fuite d'eau sur la voie publique", "Refoulement d'égout", 'Borne-fontaine endommagée', "Problème de qualité de l'eau"]
}, {
  id: 'collecte',
  name: 'Collecte et propreté',
  desc: 'Déchets, recyclage et propreté urbaine',
  icon: TrashIcon,
  subcategories: ['Collecte manquée', 'Bac de recyclage endommagé', 'Dépôt sauvage de déchets', 'Conteneur à réparer']
}, {
  id: 'stationnement',
  name: 'Stationnement et circulation',
  desc: 'Stationnement, signalisation et déneigement',
  icon: CarIcon,
  subcategories: ['Stationnement illégal', 'Déneigement à effectuer', 'Panneau de signalisation manquant']
}, {
  id: 'batiment',
  name: 'Bâtiment et urbanisme',
  desc: 'Permis, salubrité et nuisances',
  icon: Building2Icon,
  internal: true,
  subcategories: ['Construction sans permis', 'Bâtiment insalubre', 'Nuisance sonore', 'Infraction au zonage']
}, {
  id: 'animaux',
  name: 'Animaux',
  desc: 'Animaux errants, nuisances et contrôle animalier',
  icon: PawPrintIcon,
  subcategories: ['Animal errant', 'Nuisance animale', 'Animal mort sur la voie publique']
}];
const mapMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="width:20px;height:20px;border-radius:9999px;background:#4F46E5;border:3px solid white;box-shadow:0 0 0 3px rgba(79,70,229,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});
type LocationMode = 'geo' | 'address' | 'none';
export function CreateRequeteDialog({
  onClose
}: CreateRequeteDialogProps) {
  const [step, setStep] = useState(0);
  const [query, setQuery] = useState('');
  const [selectedRequester, setSelectedRequester] = useState('Hugo Langlais');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcat, setSelectedSubcat] = useState<string | null>(null);
  const [locationMode, setLocationMode] = useState<LocationMode>('geo');
  const [detailTitle, setDetailTitle] = useState('');
  const [detailDesc, setDetailDesc] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const category = CATEGORIES.find(c => c.id === selectedCategory);
  const subcategories = category?.subcategories ?? [];
  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    setAttachments(prev => [...prev, ...Array.from(files)]);
  };
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };
  const canContinue = () => {
    if (step === 0) return !!selectedRequester;
    if (step === 1) return !!selectedCategory;
    if (step === 2) return !!selectedSubcat;
    return true;
  };
  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);else onClose();
  };
  const prev = () => setStep(Math.max(0, step - 1));
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1843" data-mp="352inibvcclvj">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} data-id="element-1844" data-mp="310xg361kszsq"></div>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200" data-id="element-1845" data-mp="1392wvez9770k">
        {/* Header */}
        <div className="relative px-8 pt-6 pb-2 flex-shrink-0" data-id="element-1846" data-mp="2sqzs3douc79h">
          <button onClick={onClose} className="absolute top-5 right-5 p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" data-id="element-1847" data-mp="2spezhtm36ye9">
            <XIcon className="w-5 h-5" data-id="element-1848" data-mp="2jso3tibg5lk7" />
          </button>
          <h2 className="text-center text-lg font-bold text-[#0F172A]" data-id="element-1849" data-mp="2nrn58sxyz06w">
            Créer une requête
          </h2>
        </div>

        {/* Stepper */}
        <div className="px-8 py-5 flex-shrink-0" data-id="element-1850" data-mp="3bfwjn9suslo0">
          <div className="flex items-center" data-id="element-1851" data-mp="3ghc4p1v16s6e">
            {STEPS.map((label, i) => {
            const completed = i < step;
            const current = i === step;
            return <Fragment key={label} data-id="element-1852" data-mp="37fmfm2omg5bs" data-mpi={i}>
                  <div className="flex flex-col items-center gap-2 relative" data-id="element-1853" data-mp="37ekut6ht04gv">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${completed ? 'bg-[#4F46E5] border-[#4F46E5] text-white' : current ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-slate-300 text-slate-300'}`} data-id="element-1854" data-mp="1r1ctlf7uqso4">
                      {completed ? <CheckIcon className="w-4 h-4" data-id="element-1855" data-mp="1xieaonwqu6jp" /> : <span className={`w-2 h-2 rounded-full ${current ? 'bg-[#4F46E5]' : 'bg-slate-300'}`} data-id="element-1856" data-mp="1xgbko3vlcdez"></span>}
                    </div>
                    <span className={`text-xs font-semibold whitespace-nowrap ${current || completed ? 'text-[#0F172A]' : 'text-slate-400'}`} data-id="element-1857" data-mp="zunqzqqis975">
                      {label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 -mt-6 ${i < step ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-1858" data-mp="m2zg1qs0bqrr"></div>}
                </Fragment>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-4 overflow-y-auto flex-1" data-id="element-1859" data-mp="jxy24wjqmip">
          {/* STEP 0 — Demandeur */}
          {step === 0 && <div data-id="element-1860" data-mp="5ihi1zrkxo8f">
              <h3 className="text-xl font-bold text-[#0F172A] mb-2" data-id="element-1861" data-mp="5hzj2vdj8vwt">
                Sélectionner le demandeur
              </h3>
              <p className="text-sm text-slate-500 mb-6" data-id="element-1862" data-mp="ag0xv43xe92h">
                Recherchez le citoyen qui effectue la demande ou enregistrez un
                nouveau citoyen
              </p>

              <button onClick={() => setSelectedRequester('Hugo Langlais')} className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-colors ${selectedRequester === 'Hugo Langlais' ? 'border-[#0F172A]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1863" data-mp="jgz47t1u6so8">
                <div className="w-12 h-12 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-xs font-bold flex-shrink-0" data-id="element-1864" data-mp="y5v9xjgf7tlo">
                  bciti+
                </div>
                <div data-id="element-1865" data-mp="2mozg837jauf7">
                  <div className="flex items-center gap-2" data-id="element-1866" data-mp="2ipyyko0slxts">
                    <span className="font-bold text-slate-900" data-id="element-1867" data-mp="2iofjv1usbuu4">
                      Hugo Langlais
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-[#4F46E5]" data-id="element-1868" data-mp="28rja6st5h2nz">
                      Moi
                    </span>
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-1869" data-mp="36h4fz7mdsy67">
                    hugo.langlais@bciti.com
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-1870" data-mp="2wp8dzlh6cnwp">(111) 111-1111</div>
                </div>
              </button>

              <div className="flex items-center gap-3 my-6" data-id="element-1871" data-mp="2rpdqlrwlygge">
                <div className="flex-1 h-px bg-slate-200" data-id="element-1872" data-mp="3p5sy5lthcpea"></div>
                <span className="text-xs text-slate-400" data-id="element-1873" data-mp="3p740dh7a1p3a">
                  ou rechercher un autre utilisateur
                </span>
                <div className="flex-1 h-px bg-slate-200" data-id="element-1874" data-mp="3mi272sijcc4"></div>
              </div>

              <div className="relative" data-id="element-1875" data-mp="3ob9mkaya0l6">
                <SearchIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1876" data-mp="3utksls0j1ela" />
                <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Rechercher un utilisateur" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-1877" data-mp="dlpdlkfoswwl" />
              </div>

              {query.length > 0 && <div className="mt-2 border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 shadow-sm" data-id="element-1878" data-mp="wd04w2pbrbp0">
                  <button className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left" data-id="element-1879" data-mp="15bc24nkp57qf">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1880" data-mp="11b1k2cjmqqv7">
                      <UserPlusIcon className="w-4 h-4 text-slate-500" data-id="element-1881" data-mp="1jzu741dtrz38" />
                    </div>
                    <div data-id="element-1882" data-mp="1owc8qsduzhbc">
                      <div className="text-sm font-semibold text-slate-900" data-id="element-1883" data-mp="1owuau5n7cpr6">
                        Créer un nouveau compte
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-1884" data-mp="1fw5zxjt3v5ii">
                        Enregistrer un nouveau citoyen
                      </div>
                    </div>
                  </button>
                  {SAMPLE_USERS.map(u => <button key={u.email} onClick={() => {
              setSelectedRequester(u.name);
              setQuery('');
            }} className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left" data-id="element-1885" data-mp="1602n9k0mnohb">
                      <img src={u.avatar} alt={u.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-1886" data-mp="n80cuqekx4fi" />
                      <div data-id="element-1887" data-mp="2k67hloiazrys">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-1888" data-mp="2k5xq1whm1cqz">
                          {u.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-1889" data-mp="2b61628661229">{u.email}</div>
                        <div className="text-xs text-slate-500" data-id="element-1890" data-mp="2b3y5wvbatsjt">{u.phone}</div>
                      </div>
                    </button>)}
                </div>}
            </div>}

          {/* STEP 1 — Catégorie */}
          {step === 1 && <div data-id="element-1891" data-mp="2yu2ap6083yce">
              <h3 className="text-lg font-bold text-[#0F172A] mb-1" data-id="element-1892" data-mp="2yxpzjruq3wks">
                Catégorie de requête
              </h3>
              <p className="text-sm text-slate-500 mb-4" data-id="element-1893" data-mp="1ng6v5w5epnkn">
                Sélectionnez la catégorie qui décrit le mieux votre demande :
              </p>
              <div className="relative mb-4" data-id="element-1894" data-mp="1ig2arv1do8p5">
                <SearchIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1895" data-mp="18dot48z7fr15" />
                <input type="text" placeholder="Rechercher une catégorie" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-1896" data-mp="1dc078vwj8np6" />
              </div>
              <div className="space-y-3" data-id="element-1897" data-mp="1r2wnrod6pefv">
                {CATEGORIES.map(c => {
              const Icon = c.icon;
              const selected = selectedCategory === c.id;
              return <button key={c.id} onClick={() => {
                setSelectedCategory(c.id);
                setSelectedSubcat(null);
              }} className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 ${selected ? 'border-[#4F46E5] border-l-4 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1898" data-mp="212nedeymj9ao">
                      <Icon className="w-6 h-6 text-slate-700 flex-shrink-0 mt-0.5" data-id="element-1899" data-mp="1x5zucprneu1o" />
                      <div data-id="element-1900" data-mp="2b4puyfdofbus">
                        <div className="flex items-center gap-2" data-id="element-1901" data-mp="2b3wqjl26wyft">
                          <span className="font-bold text-slate-900" data-id="element-1902" data-mp="2664ssaev6i3p">
                            {c.name}
                          </span>
                          {c.internal && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-[#4F46E5] border border-indigo-100" data-id="element-1903" data-mp="1eep7cjp1n1f2">
                              INTERNE
                            </span>}
                        </div>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-1904" data-mp="1ne2eh5pp3dwm">{c.desc}</p>
                      </div>
                    </button>;
            })}
              </div>
            </div>}

          {/* STEP 2 — Sous-catégorie */}
          {step === 2 && <div data-id="element-1905" data-mp="36ci768mgqv4j">
              <h3 className="text-sm font-bold text-[#0F172A] mb-2" data-id="element-1906" data-mp="36byub6bo6wxd">
                Catégorie de requête
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 flex items-center gap-3 mb-6" data-id="element-1907" data-mp="2xdn4v7mji56i">
                {category && <category.icon className="w-6 h-6 text-slate-700" data-id="element-1908" data-mp="32d9gxdqmtfdq" />}
                <span className="font-bold text-slate-900" data-id="element-1909" data-mp="3q1sm3kedxc1l">
                  {category?.name}
                </span>
              </div>
              <h3 className="text-base font-bold text-[#0F172A] mb-3" data-id="element-1910" data-mp="3u2uoga3lm47s">
                Sélectionner une sous-catégorie
              </h3>
              <div className="relative mb-3" data-id="element-1911" data-mp="3l2pkk9xm5nyd">
                <SearchIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1912" data-mp="3l1ee22csa23k" />
                <input type="text" placeholder="Rechercher le sujet de votre requête" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-1913" data-mp="24nei12h26wll" />
              </div>
              <div className="space-y-2" data-id="element-1914" data-mp="1gte5nl0ldegx">
                {subcategories.map(s => {
              const selected = selectedSubcat === s;
              return <button key={s} onClick={() => setSelectedSubcat(s)} className={`w-full text-left px-4 py-3 rounded-xl border font-medium text-slate-800 transition-colors ${selected ? 'border-[#0F172A]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1915" data-mp="1bs6yno69wjbb">
                      {s}
                    </button>;
            })}
              </div>
            </div>}

          {/* STEP 3 — Lieu */}
          {step === 3 && <div data-id="element-1916" data-mp="2n8wz0qo0mgx0">
              <h3 className="text-sm font-bold text-[#0F172A] mb-2" data-id="element-1917" data-mp="2nahtyhe8s1ve">
                Emplacement de la requête
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 mb-4" data-id="element-1918" data-mp="2uspf14gq1hks">
                <div className="flex items-center gap-3" data-id="element-1919" data-mp="2zqr88i7v2x9z">
                  {category && <category.icon className="w-6 h-6 text-slate-700" data-id="element-1920" data-mp="34jtvk8m1pahq" />}
                  <span className="font-bold text-slate-900" data-id="element-1921" data-mp="39gtywbrifz55">
                    {category?.name}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-1 ml-9" data-id="element-1922" data-mp="2bw7sd1gqc7ve">
                  {selectedSubcat}
                </p>
              </div>
              <div className="h-56 rounded-xl overflow-hidden border border-slate-200 mb-5" data-id="element-1923" data-mp="2lrrj79gfsgda">
                <MapContainer center={[45.4499, -73.4653]} zoom={13} scrollWheelZoom={false} style={{
              height: '100%',
              width: '100%'
            }} data-id="element-1924" data-mp="2pue3mpas2z0g">
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1925" data-mp="r577eb8vl5si" />
                  <Marker position={[45.4499, -73.4653]} icon={mapMarkerIcon} data-id="element-1926" data-mp="10w23s8fu5lro" />
                </MapContainer>
              </div>
              <h3 className="text-base font-bold text-[#0F172A] mb-1" data-id="element-1927" data-mp="w2z7hsjoby7s">
                Localiser votre requête
              </h3>
              <p className="text-sm text-slate-500 mb-3" data-id="element-1928" data-mp="14wlzr8ooajc8">
                Utilisez votre position actuelle, entrez une adresse ou
                continuez sans adresse
              </p>

              {/* Geolocation — default option */}
              <button onClick={() => setLocationMode('geo')} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors mb-3 ${locationMode === 'geo' ? 'border-[#4F46E5] bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1929" data-mp="1evtfg2ivqq2i">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${locationMode === 'geo' ? 'border-[#4F46E5]' : 'border-slate-300'}`} data-id="element-1930" data-mp="1w4m832j0gkmy">
                  {locationMode === 'geo' && <span className="w-2 h-2 rounded-full bg-[#4F46E5]" data-id="element-1931" data-mp="1rcuhirzuuewt"></span>}
                </span>
                <CrosshairIcon className="w-5 h-5 text-[#4F46E5] flex-shrink-0" data-id="element-1932" data-mp="1dcjpvmqq0wal" />
                <span data-id="element-1933" data-mp="1hfg9ngrzyyg4">
                  <span className="flex items-center gap-2" data-id="element-1934" data-mp="1hfzgo2g402fd">
                    <span className="block text-sm font-semibold text-slate-800" data-id="element-1935" data-mp="13fygkp7o4z0y">
                      Utiliser ma géolocalisation
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-[#4F46E5] border border-indigo-100" data-id="element-1936" data-mp="18gm5n5cc75sj">
                      RECOMMANDÉ
                    </span>
                  </span>
                  <span className="block text-xs text-slate-500" data-id="element-1937" data-mp="a7baqx8yo56y">
                    Détecter automatiquement votre position actuelle
                  </span>
                </span>
              </button>

              {locationMode === 'geo' && <div className="flex items-center gap-3 p-3 rounded-xl bg-indigo-50/50 border border-indigo-100 mb-3" data-id="element-1938" data-mp="17xvtp22ioeu">
                  <MapPinIcon className="w-5 h-5 text-[#4F46E5] flex-shrink-0" data-id="element-1939" data-mp="58htctqjzu1i" />
                  <div className="text-sm" data-id="element-1940" data-mp="sz3zgfgzz48v">
                    <p className="font-semibold text-slate-800" data-id="element-1941" data-mp="xwx5n8iavy95">
                      Position détectée
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1942" data-mp="jxxh00usfjcz">
                      45.5670, -71.9970 · Windsor, QC
                    </p>
                  </div>
                </div>}

              {/* Address option */}
              <button onClick={() => setLocationMode('address')} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors mb-3 ${locationMode === 'address' ? 'border-[#4F46E5] bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1943" data-mp="34lsd967ozp4r">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${locationMode === 'address' ? 'border-[#4F46E5]' : 'border-slate-300'}`} data-id="element-1944" data-mp="2x66t3zxllqom">
                  {locationMode === 'address' && <span className="w-2 h-2 rounded-full bg-[#4F46E5]" data-id="element-1945" data-mp="2ifhjv33p6lne"></span>}
                </span>
                <MapPinIcon className="w-5 h-5 text-slate-500 flex-shrink-0" data-id="element-1946" data-mp="2de0hf35ker4p" />
                <span data-id="element-1947" data-mp="2s62cm9wowvye">
                  <span className="block text-sm font-semibold text-slate-800" data-id="element-1948" data-mp="2s6kjs1kw4ul3">
                    Entrer une adresse
                  </span>
                  <span className="block text-xs text-slate-500" data-id="element-1949" data-mp="1znew77sn253n">
                    Rechercher l'adresse la plus proche de votre requête
                  </span>
                </span>
              </button>

              {locationMode === 'address' && <div className="relative mb-3" data-id="element-1950" data-mp="3upsb5loutqj6">
                  <SearchIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1951" data-mp="3ka8o38ng8yw" />
                  <input type="text" placeholder="Rechercher l'adresse la plus proche pour votre requête" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-1952" data-mp="8dbrgmbz8snr" />
                </div>}

              {/* No address option */}
              <button onClick={() => setLocationMode('none')} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${locationMode === 'none' ? 'border-[#4F46E5] bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1953" data-mp="3kvjo3dilepse">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${locationMode === 'none' ? 'border-[#4F46E5]' : 'border-slate-300'}`} data-id="element-1954" data-mp="15448bm6qioql">
                  {locationMode === 'none' && <span className="w-2 h-2 rounded-full bg-[#4F46E5]" data-id="element-1955" data-mp="15evcf593kv8o"></span>}
                </span>
                <span data-id="element-1956" data-mp="11cqkqc27m8oo">
                  <span className="block text-sm font-semibold text-slate-800" data-id="element-1957" data-mp="wfqcbuhw0tue">
                    Sans adresse
                  </span>
                  <span className="block text-xs text-slate-500" data-id="element-1958" data-mp="rfm0ihrxcnai">
                    Continuer sans adresse
                  </span>
                </span>
              </button>
            </div>}

          {/* STEP 4 — Détails */}
          {step === 4 && <div data-id="element-1959" data-mp="2gw3hztvhchef">
              <h3 className="text-lg font-bold text-[#0F172A] mb-4" data-id="element-1960" data-mp="2guk0y12022bl">
                Détails de la requête
              </h3>
              <div className="mb-4" data-id="element-1961" data-mp="2cuhwxl8skwt5">
                <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-1962" data-mp="2jx6l1iwun8w8">
                  Titre
                </label>
                <input type="text" value={detailTitle} onChange={e => setDetailTitle(e.target.value)} placeholder="Titre de la requête" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-1963" data-mp="2owj6bz82o7wp" />
              </div>
              <div className="mb-5" data-id="element-1964" data-mp="25y6ycgswd42p">
                <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-1965" data-mp="25z8cwv94g8p6">
                  Description
                </label>
                <textarea value={detailDesc} onChange={e => setDetailDesc(e.target.value)} rows={5} placeholder="Décrivez la requête…" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent resize-none" data-id="element-1966" data-mp="1w6u8fuqnsnzb" />
              </div>

              {/* Attachments */}
              <div data-id="element-1967" data-mp="yodnztcvficm">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2" data-id="element-1968" data-mp="1cmbrg3pnludc">
                  <PaperclipIcon className="w-4 h-4 text-slate-500" data-id="element-1969" data-mp="18jp6mmreh9pn" />
                  Pièces jointes
                </label>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => handleFiles(e.target.files)} data-id="element-1970" data-mp="1miovnvitrqmc" />
                <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-slate-200 rounded-xl text-slate-500 hover:border-[#4F46E5] hover:bg-indigo-50/30 transition-colors" data-id="element-1971" data-mp="1wbw1fhhh98my">
                  <UploadCloudIcon className="w-7 h-7 text-slate-400" data-id="element-1972" data-mp="xz9uxen3qr2k" />
                  <span className="text-sm font-semibold text-slate-700" data-id="element-1973" data-mp="12wjuvo84al4n">
                    Cliquez pour téléverser des fichiers
                  </span>
                  <span className="text-xs text-slate-400" data-id="element-1974" data-mp="16wk6z27quk8j">
                    Photos, PDF ou documents — jusqu'à 10 Mo par fichier
                  </span>
                </button>

                {attachments.length > 0 && <ul className="mt-3 space-y-2" data-id="element-1975" data-mp="1lnb0qfswbtea">
                    {attachments.map((file, i) => <li key={`${file.name}-${i}`} className="flex items-center gap-3 px-3 py-2.5 border border-slate-200 rounded-lg" data-id="element-1976" data-mp="1qm4e8886nkeg" data-mpi={i}>
                        <FileIcon className="w-5 h-5 text-[#4F46E5] flex-shrink-0" data-id="element-1977" data-mp="3seiwx9ijwsvx" />
                        <div className="min-w-0 flex-1" data-id="element-1978" data-mp="9yf4nex12q1">
                          <p className="text-sm font-medium text-slate-800 truncate" data-id="element-1979" data-mp="ahxu6ce3v54">
                            {file.name}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-1980" data-mp="3k54owsbwoeo2">
                            {formatSize(file.size)}
                          </p>
                        </div>
                        <button type="button" onClick={() => removeAttachment(i)} className="text-slate-400 hover:text-red-600 flex-shrink-0" aria-label={`Retirer ${file.name}`} data-id="element-1981" data-mp="3b4ol1hr935io">
                          <XIcon className="w-4 h-4" data-id="element-1982" data-mp="2omwppfnlexa" />
                        </button>
                      </li>)}
                  </ul>}
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-slate-100 flex items-center justify-between flex-shrink-0" data-id="element-1983" data-mp="2ngf8l6fxfu47">
          <button onClick={prev} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${step === 0 ? 'invisible' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-1984" data-mp="2ii3uukmigzgp">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-1985" data-mp="cf89p5io087n" /> Précédent
          </button>
          <button onClick={next} disabled={!canContinue()} className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-1986" data-mp="hds13lg12c7e">
            {step === STEPS.length - 1 ? 'Créer la requête' : 'Suivant'}
            {step < STEPS.length - 1 && <ChevronRightIcon className="w-4 h-4" data-id="element-1987" data-mp="10c47izfk17za" />}
          </button>
        </div>
      </div>
    </div>;
}